import React, { useState, useEffect, useContext, useRef } from 'react';
import Swal from '../../utils/swal';
import { AppBar, Badge, Button, Checkbox, Fab, FormControlLabel, Grow, IconButton, Switch, Toolbar } from "@material-ui/core";
import AppContext from "../../../appContext";
import { useApi } from "../../../hooks/useApi";
import { Card } from "react-bootstrap";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Plus, ArrowDownUp, Images, NodeMinus } from "react-bootstrap-icons";
import EmptyState from "../../../shared/components/generic/empty-state/empty-state";
import { getNewIds } from "../../utils/diff-items";
import SortableCarrosselCard from "./components/sortable-carrossel-card";
import CarrosselForm from "./components/carrossel-form";
import CarrosselOrderModal from "./components/carrossel-order-modal";
import CustomButton from "../../../components/buttons/button";
import { getLab } from "../../../Functions/Laboratorio/get_lab_by_cnpj";
import { updateLabCarrosselFlag } from "../../../Functions/Laboratorio/update_lab_carrossel_flag";
import './carrossel.css';

const MAX_CAROUSEL_IMAGES = 10;

const Carrossel = ({ selectedLab, hasUploadPermission, isLabUser, isActive }) => {
	const context = useContext(AppContext);
	const [images, setImages] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showForm, setShowForm] = useState(false);
	const [editingImage, setEditingImage] = useState(null);
	const [showOrderModal, setShowOrderModal] = useState(false);
	const [hasCarouselPermission, setHasCarouselPermission] = useState(false);
	const [carrosselAtivo, setCarrosselAtivo] = useState(false);
	const [flagLoaded, setFlagLoaded] = useState(false);
	const [flagLoading, setFlagLoading] = useState(false);
	const [cacheKey, setCacheKey] = useState(Date.now());
	const [selectedIds, setSelectedIds] = useState([]);
	const lastSelectedCount = useRef(0);
	const hasLoadedOnce = useRef(false);
	const [newIds, setNewIds] = useState([]);
	const isFirstLoad = useRef(true);

	useEffect(() => {
		if (selectedIds.length > 0) {
			lastSelectedCount.current = selectedIds.length;
		}
	}, [selectedIds]);

	const { get, del, post } = useApi();
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

	useEffect(() => {
		const loadAll = async () => {
			setFlagLoaded(false);
			context.showLoading('Carregando carrossel...');
			try {
				await Promise.all([
					checkCarouselPermission(),
					fetchCarrosselFlag(),
					fetchImages(false),
				]);
			} finally {
				context.hideLoading();
			}
		};

		loadAll();
		hasLoadedOnce.current = true;
		setSelectedIds([]);
	}, [selectedLab]);

	useEffect(() => {
		if (isActive && hasLoadedOnce.current) {
			const reload = async () => {
				context.showLoading('Carregando carrossel...');
				try {
					await Promise.all([
						fetchCarrosselFlag(),
						fetchImages(false),
					]);
				} finally {
					context.hideLoading();
				}
			};
			reload();
			setSelectedIds([]);
		}
		if (!isActive) {
			setSelectedIds([]);
		}
	}, [isActive]);

	const toggleSelect = (id, checked) => {
		setSelectedIds((prev) => {
			if (checked) {
				return prev.includes(id) ? prev : [...prev, id];
			}
			return prev.filter((item) => item !== id);
		});
	};

	const toggleSelectAll = (checked) => {
		setSelectedIds(checked ? images.map((img) => img.id) : []);
	};

	const allSelected = images.length > 0 && selectedIds.length === images.length;
	const someSelected = selectedIds.length > 0 && selectedIds.length < images.length;

	const checkCarouselPermission = async () => {
		const labCnpj = isLabUser ? context.userSession.conta : selectedLab?.cnpj;

		try {
			const params = new URLSearchParams({ lab_cnpj: labCnpj });
			const data = await get(
				`${process.env.REACT_APP_LABS_API}check_carousel_permission.php?${params}`
			);

			if (data.status === 200) {
				setHasCarouselPermission(data.has_permission);
			} else {
				setHasCarouselPermission(false);
			}
		} catch (error) {
			setHasCarouselPermission(false);
		}
	};

	const fetchCarrosselFlag = async () => {
		const labCnpj = isLabUser ? context.userSession.conta : selectedLab?.cnpj;
		if (!labCnpj) {
			setCarrosselAtivo(false);
			setFlagLoaded(true);
			return;
		}

		try {
			const data = await getLab(labCnpj);
			if (data && data.item) {
				setCarrosselAtivo(!!data.item.carrosselAtivo);
			} else {
				setCarrosselAtivo(false);
			}
		} catch (error) {
			setCarrosselAtivo(false);
		} finally {
			setFlagLoaded(true);
		}
	};

	const handleToggleCarrossel = async (event) => {
		const newValue = event.target.checked;
		const labCnpj = isLabUser ? context.userSession.conta : selectedLab?.cnpj;

		if (!labCnpj) {
			return;
		}

		setFlagLoading(true);
		setCarrosselAtivo(newValue);
		context.showLoading('Atualizando flag do carrossel...');

		try {
			const result = await updateLabCarrosselFlag(labCnpj, newValue);
			context.hideLoading();

			if (result && result.status === 200) {
				await Swal.fire({
					title: 'Sucesso!',
					text: newValue
						? 'Carrossel ativado para este laboratório.'
						: 'Carrossel desativado para este laboratório.',
					icon: 'success',
				});
			} else {
				setCarrosselAtivo(!newValue);
				await Swal.fire(
					'Erro!',
					'Não foi possível atualizar a flag do carrossel no OEC.',
					'error'
				);
			}
		} catch (error) {
			context.hideLoading();
			setCarrosselAtivo(!newValue);
			await Swal.fire(
				'Erro!',
				'Não foi possível atualizar a flag do carrossel no OEC.',
				'error'
			);
		} finally {
			setFlagLoading(false);
		}
	};

	const handleDeleteSelected = async () => {
		if (selectedIds.length === 0) {
			return;
		}

		const count = selectedIds.length;
		const result = await Swal.fire({
			title: `Apagar ${count} ${count === 1 ? 'imagem' : 'imagens'}?`,
			text: 'Essa ação irá remover as imagens selecionadas do carrossel. Não é possível desfazer.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#3085d6',
			confirmButtonText: 'Sim, apagar',
			cancelButtonText: 'Cancelar',
		});

		if (!(result.isConfirmed || result.value)) {
			return;
		}

		context.showLoading(`Apagando ${count} ${count === 1 ? 'imagem' : 'imagens'}...`);

		try {
			const results = await Promise.all(
				selectedIds.map((id) =>
					del(`${process.env.REACT_APP_LABS_API}delete_carousel_image.php?image_id=${id}`)
						.then((json) => ({ id, ok: json?.status === 200, json }))
						.catch(() => ({ id, ok: false }))
				)
			);

			setSelectedIds([]);
			setCacheKey(Date.now());
			await fetchImages(false);
			context.hideLoading();

			const failed = results.filter((r) => !r.ok);
			if (failed.length === 0) {
				await Swal.fire('Sucesso!', 'Imagens excluídas com sucesso.', 'success');
			} else if (failed.length < selectedIds.length) {
				await Swal.fire(
					'Parcial',
					`${selectedIds.length - failed.length} de ${selectedIds.length} imagens foram excluídas.`,
					'warning'
				);
			} else {
				await Swal.fire('Erro!', 'Erro ao excluir imagens do carrossel.', 'error');
			}
		} catch (error) {
			context.hideLoading();
			await Swal.fire('Erro!', 'Erro ao excluir imagens do carrossel.', 'error');
		}
	};

	const preloadImages = (items) => {
		const promises = items.map((item) => {
			return new Promise((resolve) => {
				const img = new Image();
				img.onload = resolve;
				img.onerror = resolve;
				img.src = `${process.env.REACT_APP_LABS_API}get_carousel_image.php?image_id=${item.id}&type=thumbnail&t=${cacheKey}`;
			});
		});
		return Promise.all(promises);
	};

	const fetchImages = async (showLoadingMessage = true) => {
		setLoading(true);
		if (showLoadingMessage) {
			context.showLoading('Carregando imagens do carrossel...');
		}

		const labCnpj = isLabUser ? context.userSession.conta : selectedLab?.cnpj;

		try {
			const params = new URLSearchParams({ lab_cnpj: labCnpj });
			const json = await get(
				`${process.env.REACT_APP_LABS_API}get_carousel_images.php?${params}`
			);

			if (json?.status === 200 && json.items) {
				if (isFirstLoad.current) {
					setNewIds(json.items.map(i => i.id));
					isFirstLoad.current = false;
				} else {
					setNewIds(getNewIds(images, json.items));
				}
				await preloadImages(json.items);
				setImages(json.items);
			}
		} catch (error) {
		} finally {
			if (showLoadingMessage) {
				context.hideLoading();
			}
			setLoading(false);
		}
	};

	const handleAddImage = () => {
		if (images.length >= MAX_CAROUSEL_IMAGES) {
			Swal.fire(
				'Limite atingido',
				'Você alcançou o limite de imagens no carrossel. Exclua uma imagem antes de adicionar a próxima.',
				'warning'
			);
			return;
		}
		setEditingImage(null);
		setShowForm(true);
	};

	const handleEditImage = (image) => {
		setEditingImage(image);
		setShowForm(true);
	};

	const handleDeleteImage = async (imageId) => {
		const result = await Swal.fire({
			title: 'Atenção',
			text: 'Tem certeza que deseja excluir esta imagem do carrossel?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Sim',
			cancelButtonText: 'Não'
		});

		if (result.isConfirmed || result.value) {
			context.showLoading('Excluindo imagem...');

			try {
				const json = await del(
					`${process.env.REACT_APP_LABS_API}delete_carousel_image.php?image_id=${imageId}`
				);

				if (json?.status === 200) {
					await fetchImages(false);
					context.hideLoading();
					await Swal.fire('Sucesso!', 'Imagem excluída com sucesso.', 'success');
				} else {
					context.hideLoading();
					await Swal.fire('Erro!', json?.message || 'Erro ao excluir imagem.', 'error');
				}
			} catch (error) {
				context.hideLoading();
				await Swal.fire('Erro!', 'Erro ao excluir imagem.', 'error');
			}
		}
	};

	const handleFormClose = () => {
		setShowForm(false);
		setEditingImage(null);
	};

	const handleFormSave = async () => {
		setCacheKey(Date.now());
		await fetchImages(false);
	};

	const handleDragEnd = async (event) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIndex = images.findIndex(i => i.id === active.id);
		const newIndex = images.findIndex(i => i.id === over.id);
		const reordered = arrayMove(images, oldIndex, newIndex);

		setImages(reordered);

		const labCnpj = isLabUser ? context.userSession.conta : selectedLab?.cnpj;
		const newOrder = reordered.map((img, index) => ({ id: img.id, ordem: index + 1 }));

		context.showLoading('Salvando ordem...');

		try {
			const json = await post(
				`${process.env.REACT_APP_LABS_API}update_carousel_order.php`,
				{
					lab_cnpj: labCnpj,
					images_order: newOrder
				}
			);

			if (json?.status === 200) {
				await fetchImages(false);
				context.hideLoading();
			} else {
				context.hideLoading();
				await Swal.fire('Erro!', json?.message || 'Erro ao atualizar ordem.', 'error');
			}
		} catch (error) {
			context.hideLoading();
			await Swal.fire('Erro!', 'Erro ao atualizar ordem.', 'error');
		}
	};

	const handleOrderSave = async (newOrder) => {
		const labCnpj = isLabUser ? context.userSession.conta : selectedLab?.cnpj;

		context.showLoading('Salvando ordem...');

		try {
			const json = await post(
				`${process.env.REACT_APP_LABS_API}update_carousel_order.php`,
				{
					lab_cnpj: labCnpj,
					images_order: newOrder
				}
			);

			if (json?.status === 200) {
				await fetchImages(false);
				context.hideLoading();
				await Swal.fire('Sucesso!', 'Ordem atualizada com sucesso.', 'success');
				setShowOrderModal(false);
			} else {
				context.hideLoading();
				await Swal.fire('Erro!', json?.message || 'Erro ao atualizar ordem.', 'error');
			}
		} catch (error) {
			context.hideLoading();
			await Swal.fire('Erro!', 'Erro ao atualizar ordem.', 'error');
		}
	};

	const getImageUrl = (imageId, type = 'arquivo') => {
		return `${process.env.REACT_APP_LABS_API}get_carousel_image.php?image_id=${imageId}&type=${type}&t=${cacheKey}`;
	};

	return (
		<div className="carrossel-page">
			{showForm && (
				<CarrosselForm
					image={editingImage}
					labCnpj={isLabUser ? context.userSession.conta : selectedLab?.cnpj}
					onClose={handleFormClose}
					onSave={handleFormSave}
					getImageUrl={getImageUrl}
				/>
			)}

			{showOrderModal && (
				<CarrosselOrderModal
					images={images}
					onClose={() => setShowOrderModal(false)}
					onSave={handleOrderSave}
					getImageUrl={getImageUrl}
				/>
			)}

			{((hasCarouselPermission && flagLoaded) || (hasUploadPermission && images.length > 0)) && (
				<div className="carrossel-header-actions">
					{hasCarouselPermission && flagLoaded && (
						<Toolbar variant="dense" disableGutters className="carrossel-toolbar carrossel-toolbar--switch">
							<FormControlLabel
								control={
									<Switch
										checked={carrosselAtivo}
										onChange={handleToggleCarrossel}
										disabled={flagLoading}
										color="primary"
										name="carrosselAtivo"
									/>
								}
								label={`Carrossel ${carrosselAtivo ? 'Ativo' : 'Inativo'}`}
								className="carrossel-flag-toggle"
							/>
						</Toolbar>
					)}

					{hasUploadPermission && images.length > 0 && (
						<Toolbar variant="dense" disableGutters className="carrossel-toolbar carrossel-toolbar--actions">
							<FormControlLabel
								control={
									<Checkbox
										checked={allSelected}
										indeterminate={someSelected}
										onChange={(event) => toggleSelectAll(event.target.checked)}
										color="primary"
									/>
								}
								label={allSelected ? 'Desmarcar todas' : 'Selecionar todas'}
								className="carrossel-select-all"
							/>

							<div className="carrossel-toolbar__spacer" />

							{selectedIds.length > 0 ? (
								<Button
									variant="outlined"
									size="large"
									onClick={handleDeleteSelected}
									className="carrossel-bulk-delete-btn"
									startIcon={<NodeMinus size={22} />}
								>
									{`Apagar ${selectedIds.length} selecionada${selectedIds.length !== 1 ? 's' : ''}`}
								</Button>
							) : hasCarouselPermission && images.length > 1 ? (
								<Button
									variant="outlined"
									size="large"
									onClick={() => setShowOrderModal(true)}
									className="carrossel-order-btn"
									id="carrossel-order-btn"
									startIcon={<ArrowDownUp size={22} />}
								>
									Ordem do Carrossel
								</Button>
							) : null}
						</Toolbar>
					)}
				</div>
			)}

			{images.length > 0 && (
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToWindowEdges]}>
				<SortableContext items={images.map(i => i.id)} strategy={rectSortingStrategy}>
					<div className="carrossel__cards-grid">
						{hasCarouselPermission && (
							<Card
								className="carrossel__add-card"
								onClick={handleAddImage}
								id="carrossel-add-image-card">
								<Card.Body className="d-flex flex-column justify-content-center align-items-center">
									<Plus size='60' className="text-primary mb-3"/>
									<p className="text-muted">Clique aqui para adicionar uma nova imagem ao carrossel</p>
								</Card.Body>
							</Card>
						)}

						{images.map((image, index) => {
							const isNew = newIds.includes(image.id);
							return isNew ? (
								<Grow key={image.id} in={true} style={{ transformOrigin: '0 0 0' }} timeout={500 + index * 100}>
									<div>
										<SortableCarrosselCard
											image={image}
											imageUrl={getImageUrl(image.id, 'thumbnail')}
											hasEditPermission={hasCarouselPermission}
											selected={selectedIds.includes(image.id)}
											onSelectChange={(checked) => toggleSelect(image.id, checked)}
											onEdit={() => handleEditImage(image)}
											onDelete={() => handleDeleteImage(image.id)}
										/>
									</div>
								</Grow>
							) : (
								<SortableCarrosselCard
									key={image.id}
									image={image}
									imageUrl={getImageUrl(image.id, 'thumbnail')}
									hasEditPermission={hasCarouselPermission}
									selected={selectedIds.includes(image.id)}
									onSelectChange={(checked) => toggleSelect(image.id, checked)}
									onEdit={() => handleEditImage(image)}
									onDelete={() => handleDeleteImage(image.id)}
								/>
							);
						})}
					</div>
				</SortableContext>
			</DndContext>
			)}

			{images.length === 0 && !loading && (
				<EmptyState
					icon={Images}
					title="Nenhuma imagem no carrossel"
					description={hasCarouselPermission ? "Adicione imagens para exibir no carrossel das óticas." : null}
					actionLabel={hasCarouselPermission ? "Adicionar imagem" : null}
					actionIcon={Plus}
					onAction={handleAddImage}
				/>
			)}
		{hasCarouselPermission && (
				<AppBar position="fixed" className="carrossel-bottom-bar">
					<Toolbar className="carrossel-bottom-bar__toolbar">
						{images.length > 0 && (
							<FormControlLabel
								control={
									<Checkbox
										checked={allSelected}
										indeterminate={someSelected}
										onChange={(event) => toggleSelectAll(event.target.checked)}
										size="small"
										className="carrossel-bottom-bar__checkbox"
									/>
								}
								label={allSelected ? 'Desmarcar' : 'Selecionar'}
								className="carrossel-bottom-bar__select"
							/>
						)}

						<Fab
							aria-label="adicionar"
							className="carrossel-bottom-bar__fab"
							onClick={handleAddImage}
						>
							<Plus size={24} />
						</Fab>

						<div style={{ flexGrow: 1 }} />

						{selectedIds.length > 0 ? (
							<IconButton className="carrossel-bottom-bar__action-btn" onClick={handleDeleteSelected}>
								<Badge badgeContent={selectedIds.length} color="error">
									<NodeMinus size={22} />
								</Badge>
							</IconButton>
						) : images.length > 1 ? (
							<IconButton className="carrossel-bottom-bar__action-btn" onClick={() => setShowOrderModal(true)}>
								<ArrowDownUp size={22} />
							</IconButton>
						) : null}
					</Toolbar>
				</AppBar>
			)}
		</div>
	);
};

export default Carrossel;